import { useMemo } from "react";
import { GiPadlock } from "react-icons/gi";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { BuildingType, BuildingState, BuildingCatalogEntry } from "../types/castle";
import { buildingDisplayName } from "../lib/buildingNames";

// Diagonal map rework (2026-06-15): a single wide 16:9 painterly landscape
// with empty building plots arranged diagonally — the keep on the large
// circular platform upper-right, the main buildings across the central grid,
// and the resource buildings in the lower-left extraction district. Each plot
// is an empty terrace in the art; we overlay a labelled tap-target on top, so
// it's always clear which building sits where (no per-building sprite art
// needed). Pan/zoom via react-zoom-pan-pinch; zoom-out is clamped to the map
// bounds so the surrounding void never shows.

const FIRE_BG = "/scenes/fire/bg-fire-walled.jpg?v=1";

// Plot centre as (x, y) fractions of the scene image, 0..1. First-pass
// eyeballed layout on the new walled fire map (2026-06-16); the owner will
// refine with pixel coordinates. Defensive buildings (Wall/ArrowTower/
// TrapField) are not placed yet — their sprites are still to be generated.
const PLOT_POS: Record<number, { x: number; y: number }> = {
  [BuildingType.Castle]: { x: 0.45, y: 0.3 },

  [BuildingType.MageTower]: { x: 0.27, y: 0.2 },
  [BuildingType.HeroHall]: { x: 0.6, y: 0.19 },
  [BuildingType.Barracks]: { x: 0.73, y: 0.27 },
  [BuildingType.ArcheryRange]: { x: 0.2, y: 0.34 },
  [BuildingType.WarHall]: { x: 0.36, y: 0.42 },
  [BuildingType.Hospital]: { x: 0.55, y: 0.4 },
  [BuildingType.TempleOfResurrection]: { x: 0.7, y: 0.42 },
  [BuildingType.Academy]: { x: 0.3, y: 0.52 },
  [BuildingType.SignatureForge]: { x: 0.48, y: 0.53 },
  [BuildingType.BuilderHut]: { x: 0.64, y: 0.54 },
  [BuildingType.Warehouse]: { x: 0.78, y: 0.5 },

  [BuildingType.GoldMine]: { x: 0.2, y: 0.6 },
  [BuildingType.Quarry]: { x: 0.35, y: 0.64 },
  [BuildingType.ManaAltar]: { x: 0.52, y: 0.66 },
  [BuildingType.Lumberjack]: { x: 0.68, y: 0.66 },
};

const ALL_TYPES = Object.keys(PLOT_POS).map((k) => Number(k));

// Painterly building sprites (transparent PNGs sliced from the fire collage),
// keyed by building type → file slug under /scenes/fire/buildings/.
const SPRITE_SLUG: Record<number, string> = {
  [BuildingType.Castle]: "castle",
  [BuildingType.MageTower]: "mage-tower",
  [BuildingType.HeroHall]: "hero-hall",
  [BuildingType.Barracks]: "barracks",
  [BuildingType.ArcheryRange]: "archery-range",
  [BuildingType.WarHall]: "war-hall",
  [BuildingType.Hospital]: "hospital",
  [BuildingType.TempleOfResurrection]: "temple",
  [BuildingType.Academy]: "academy",
  [BuildingType.SignatureForge]: "signature",
  [BuildingType.BuilderHut]: "builder-hut",
  [BuildingType.Warehouse]: "warehouse",
  [BuildingType.GoldMine]: "gold-mine",
  [BuildingType.Lumberjack]: "lumberjack",
  [BuildingType.Quarry]: "quarry",
  [BuildingType.ManaAltar]: "mana-altar",
};

// Sprite width as a fraction of the scene width. The keep is larger so it
// reads as the centrepiece; every other building fills its platform.
const SPRITE_W = 0.07;
const CASTLE_W = 0.115;
// How far below the plot point the sprite's base sits (fraction of scene
// height), so buildings look planted on the platform, not floating above it.
const BASE_DROP = 0.02;

interface Props {
  // Player element 0..3 (Fire/Water/Air/Earth). Only Fire has the diagonal
  // map so far; everyone sees it until the other elements are repainted.
  element: number | undefined;
  castleLevel: number;
  buildings: BuildingState[];
  catalogGet: (type: number) => BuildingCatalogEntry | undefined;
  onOpenBuilding: (type: number) => void;
}

export function CastleScene({
  element,
  castleLevel,
  buildings,
  catalogGet,
  onOpenBuilding,
}: Props) {
  // The map is large and meant to be explored up close: at scale 1 the image
  // fills the frame width (whole map width visible), so MIN_SCALE = 5 means
  // the most you can ever zoom out shows ~1/5 (≈20%) of the map width. At that
  // scale the 16:9 art also overflows the frame vertically on a portrait phone,
  // so no void shows. limitToBounds pins the edges.
  const MIN_SCALE = 4;
  const MAX_SCALE = 10;

  // Map building type -> render meta (level, locked, name).
  const zoneMeta = useMemo(() => {
    const m = new Map<number, { level: number; locked: boolean; name: string }>();
    for (const t of ALL_TYPES) {
      const state = buildings.find((b) => b.type === t);
      const def = catalogGet(t);
      const revealAt = def?.revealAtCastleLevel ?? 1;
      const level = state?.level ?? 0;
      const locked = level === 0 && castleLevel < revealAt;
      const name = buildingDisplayName(t, def?.name ?? `#${t}`, element);
      m.set(t, { level, locked, name });
    }
    return m;
  }, [buildings, catalogGet, castleLevel, element]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-tg-bg">
      <TransformWrapper
        initialScale={MIN_SCALE}
        minScale={MIN_SCALE}
        maxScale={MAX_SCALE}
        wheel={{ step: 0.4 }}
        doubleClick={{ mode: "toggle", step: 2 }}
        pinch={{ step: 5 }}
        limitToBounds
        centerOnInit
        // No elastic overscroll and no fling past the edges — dragging or
        // zooming the map can never expose the void behind it.
        alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{ width: "100%" }}
        >
          {/* The image renders at frame width (scale 1 = contain); the cover
              scale above zooms it so it fills the frame's height and overflows
              horizontally, panning left/right with the edges pinned. Plots are
              positioned in % of this image box. */}
          <div className="relative w-full">
            <img
              src={FIRE_BG}
              alt="Замок"
              draggable={false}
              className="block w-full select-none"
            />
            {ALL_TYPES.map((type) => {
              const meta = zoneMeta.get(type)!;
              const pos = PLOT_POS[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => onOpenBuilding(type)}
                  aria-label={meta.name}
                  // Base-seated on the plot point: the sprite's BOTTOM sits
                  // just below the platform centre, so the building looks
                  // planted rather than floating. Sprite fills the button
                  // width; a small badge shows level / lock.
                  className="absolute -translate-x-1/2 -translate-y-full select-none focus:outline-none"
                  style={{
                    left: `${pos.x * 100}%`,
                    top: `${(pos.y + BASE_DROP) * 100}%`,
                    width: `${(type === BuildingType.Castle ? CASTLE_W : SPRITE_W) * 100}%`,
                  }}
                >
                  <img
                    src={`/scenes/fire/buildings/${SPRITE_SLUG[type]}.png?v=2`}
                    alt={meta.name}
                    draggable={false}
                    className={
                      "block w-full select-none drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)] transition-transform active:scale-95 " +
                      (meta.locked ? "opacity-45 grayscale" : "")
                    }
                  />
                  <SpriteBadge level={meta.level} locked={meta.locked} />
                </button>
              );
            })}
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

// Small badge under a building sprite: level when built, lock when gated,
// nothing when available-but-not-yet-built (the sprite itself reads as a plot).
function SpriteBadge({ level, locked }: { level: number; locked: boolean }) {
  if (!locked && level === 0) return null;
  return (
    <span className="pointer-events-none absolute bottom-0 left-1/2 inline-flex -translate-x-1/2 translate-y-1/4 items-center justify-center rounded-full bg-black/80 px-1 text-[8px] font-bold leading-none text-white shadow-md backdrop-blur-sm">
      {locked ? <GiPadlock size={8} /> : <span className="py-0.5">{level}</span>}
    </span>
  );
}
